import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Pagination } from '../../components/Pagination';
import { StarRatingDisplay } from '../../components/shared/StarRatingDisplay';
import { apiService } from '../../services/api';
import type { ChatLogWithResolution, DashboardStats } from '../../types';
import { buildDownloadFilename } from './exportFilename';

// ---------------------------------------------------------------------------
// ユーティリティ
// ---------------------------------------------------------------------------

function formatDatetime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function currentYearMonth(): string {
  return new Date().toISOString().slice(0, 7);
}

function buildYearMonthOptions(): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = [{ value: '', label: '全期間' }];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = `${d.getFullYear()}年${d.getMonth() + 1}月`;
    options.push({ value, label });
  }
  return options;
}

// ---------------------------------------------------------------------------
// サブコンポーネント
// ---------------------------------------------------------------------------

interface StatCardProps {
  label: string;
  value: string | number;
  sub?: string;
}

function StatCard({ label, value, sub }: StatCardProps) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <p className="text-sm font-medium text-slate-500">{label}</p>
      <p className="mt-1 text-3xl font-bold text-slate-900">{value}</p>
      {sub && <p className="mt-1 text-xs text-slate-400">{sub}</p>}
    </div>
  );
}

interface BarChartRowProps {
  label: string;
  count: number;
  max: number;
}

function BarChartRow({ label, count, max }: BarChartRowProps) {
  const pct = max === 0 ? 0 : Math.round((count / max) * 100);
  return (
    <div className="flex items-center gap-3">
      <span className="w-36 shrink-0 truncate text-right text-sm text-slate-600">{label}</span>
      <div className="flex-1 rounded-full bg-slate-100">
        <div className="h-4 rounded-full bg-blue-500 transition-all" style={{ width: `${pct}%` }} />
      </div>
      <span className="w-10 shrink-0 text-right text-sm font-medium text-slate-700">{count}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// メインページ
// ---------------------------------------------------------------------------

export const ChatLogsPage: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [sessions, setSessions] = useState<ChatLogWithResolution[]>([]);
  const [yearMonth, setYearMonth] = useState('');
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const cursorMapRef = useRef(new Map<number, string>());
  const [totalCount, setTotalCount] = useState(0);
  const totalPages = Math.ceil(totalCount / 50) || 1;

  const yearMonthOptions = useMemo(() => buildYearMonthOptions(), []);

  // 統計情報の読み込み
  useEffect(() => {
    setStatsLoading(true);
    apiService
      .getDashboardStats(yearMonth || undefined)
      .then(setStats)
      .catch(() => setStats(null))
      .finally(() => setStatsLoading(false));
  }, [yearMonth]);

  // セッション一覧の読み込み
  const fetchPage = useCallback(
    async (page: number) => {
      const cursorMap = cursorMapRef.current;
      setLoading(true);
      setError(null);
      try {
        if (page > 1 && !cursorMap.has(page)) {
          let startPage = 1;
          for (let p = page - 1; p >= 2; p--) {
            if (cursorMap.has(p)) {
              startPage = p;
              break;
            }
          }
          for (let p = startPage; p < page; p++) {
            const cursor = p === 1 ? undefined : cursorMap.get(p);
            const intermediate = await apiService.getSessions({
              yearMonth: yearMonth || 'all',
              limit: 50,
              lastKey: cursor,
            });
            if (intermediate.lastKey) {
              cursorMap.set(p + 1, intermediate.lastKey);
            }
            if (!intermediate.lastKey && p + 1 <= page) {
              break;
            }
          }
        }

        const lastKey = page === 1 ? undefined : cursorMap.get(page);
        const res = await apiService.getSessions({
          yearMonth: yearMonth || 'all',
          limit: 50,
          lastKey,
        });
        setSessions(res.sessions);
        setTotalCount(res.totalCount);
        setCurrentPage(page);
        if (res.lastKey) {
          cursorMap.set(page + 1, res.lastKey);
        }
      } catch {
        setError('データの取得に失敗しました');
      } finally {
        setLoading(false);
      }
    },
    [yearMonth],
  );

  const handleDownloadCsv = useCallback(async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      const ym = yearMonth || 'all';
      const blob = await apiService.exportSessionsCsv(ym);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = buildDownloadFilename(ym, new Date());
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch {
      setDownloadError('CSVのダウンロードに失敗しました');
    } finally {
      setDownloading(false);
    }
  }, [yearMonth]);

  useEffect(() => {
    cursorMapRef.current.clear();
    setCurrentPage(1);
    fetchPage(1);
  }, [fetchPage]);

  const categoryBreakdown = stats?.categoryBreakdown ?? [];
  const categoryMax = categoryBreakdown[0]?.count ?? 0;

  const isAllTime = yearMonth === '';
  const isCurrentMonth = yearMonth === currentYearMonth();
  const showTodayCard = isAllTime || isCurrentMonth;
  const selectedMonthLabel = yearMonthOptions.find((o) => o.value === yearMonth)?.label ?? '';
  const statsSub = isAllTime ? '全期間' : selectedMonthLabel;
  const actionSub = isAllTime ? '当月' : selectedMonthLabel;

  return (
    <div className="space-y-6 p-6">
      <h1 className="text-2xl font-bold text-slate-900">チャットログ</h1>

      {/* ── サマリーカード ── */}
      <section
        className={`grid gap-4 ${showTodayCard ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2 sm:grid-cols-3'}`}
      >
        {statsLoading ? (
          Array.from({ length: showTodayCard ? 4 : 3 }).map((_, i) => (
            <div key={i} className="h-24 animate-pulse rounded-xl bg-slate-100" />
          ))
        ) : (
          <>
            <StatCard label="総会話数" value={stats?.totalChatLogs ?? 0} sub={statsSub} />
            {showTodayCard && (
              <StatCard label="本日の会話" value={stats?.todayChatLogs ?? 0} sub="今日" />
            )}
            <StatCard
              label="平均評価"
              value={stats && stats.ratingTotal > 0 ? `${stats.ratingAverage.toFixed(1)} / 5` : '—'}
              sub={`${stats?.ratingTotal ?? 0}件の評価`}
            />
            <StatCard
              label="症状が見つからなかった件数"
              value={stats?.noSymptomCount ?? 0}
              sub={statsSub}
            />
          </>
        )}
      </section>

      {/* ── 評価分布 ── */}
      {!statsLoading && stats && stats.ratingTotal > 0 && (
        <section>
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="mb-4 text-sm font-semibold text-slate-700">評価の分布</h2>
            <div className="space-y-2">
              {(['5', '4', '3', '2', '1'] as const).map((star) => (
                <BarChartRow
                  key={star}
                  label={`★${star}`}
                  count={stats.ratingCounts[star]}
                  max={Math.max(...Object.values(stats.ratingCounts))}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── アクション統計 ── */}
      {!statsLoading && stats?.actionStats && (
        <section className="grid grid-cols-3 gap-4">
          <StatCard
            label="修理依頼（はい）"
            value={stats.actionStats.repair_yes ?? 0}
            sub={actionSub}
          />
          <StatCard
            label="電話番号クリック"
            value={stats.actionStats.phone_click ?? 0}
            sub={actionSub}
          />
          <StatCard
            label="修理フォームクリック"
            value={stats.actionStats.repair_form_click ?? 0}
            sub={actionSub}
          />
        </section>
      )}

      {/* ── 質問傾向 ── */}
      {!statsLoading && categoryBreakdown.length > 0 && (
        <section>
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="mb-4 text-sm font-semibold text-slate-700">
              カテゴリ別質問件数（{isAllTime ? '直近3ヶ月' : selectedMonthLabel}）
            </h2>
            <div className="space-y-2">
              {categoryBreakdown.map((item) => (
                <BarChartRow
                  key={item.category}
                  label={item.category}
                  count={item.count}
                  max={categoryMax}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── 一覧テーブル ── */}
      <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
        {/* フィルター */}
        <div className="flex items-center gap-4 border-b border-slate-200 px-5 py-4">
          <label className="text-sm font-medium text-slate-600" htmlFor="yearMonth">
            表示月
          </label>
          <select
            id="yearMonth"
            value={yearMonth}
            onChange={(e) => setYearMonth(e.target.value)}
            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {yearMonthOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <span className="ml-auto text-sm text-slate-500">{totalCount} 件</span>
          <button
            type="button"
            onClick={handleDownloadCsv}
            disabled={downloading}
            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {downloading ? 'ダウンロード中…' : 'CSVダウンロード'}
          </button>
        </div>
        {downloadError && <p className="px-5 pb-3 text-sm text-red-600">{downloadError}</p>}

        {/* テーブル */}
        {loading ? (
          <div className="space-y-3 p-5">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-10 animate-pulse rounded bg-slate-100" />
            ))}
          </div>
        ) : error ? (
          <p className="p-6 text-center text-sm text-red-600">{error}</p>
        ) : sessions.length === 0 ? (
          <p className="p-6 text-center text-sm text-slate-500">該当する会話ログがありません</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <th className="px-5 py-3">日時</th>
                    <th className="px-5 py-3">カテゴリ</th>
                    <th className="px-5 py-3">評価</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {sessions.map((s) => (
                    <tr key={s.sessionId} className="hover:bg-slate-50">
                      <td className="whitespace-nowrap px-5 py-3 text-slate-700">
                        {formatDatetime(s.timestamp)}
                      </td>
                      <td className="px-5 py-3 text-slate-700">{s.category || '—'}</td>
                      <td className="px-5 py-3">
                        <StarRatingDisplay rating={s.rating} />
                      </td>
                      <td className="px-5 py-3 text-right">
                        <Link
                          to={`/logs/chat/${s.sessionId}`}
                          className="rounded px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50"
                        >
                          詳細 →
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* ページネーション */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-slate-200 px-5 py-3">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={fetchPage}
                  isLoading={loading}
                />
                <span className="text-sm text-slate-500">
                  全{totalCount}件中 {(currentPage - 1) * 50 + 1}-
                  {Math.min(currentPage * 50, totalCount)}件
                </span>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
};
